import dayjs from 'dayjs';
import {
  CUTOFF_KEY,
  SCHEDULE_SLOT_KEYS,
  WEEKDAY_KEYS,
  getDefaultCutoffForWeekday,
} from './constants';

export const STORAGE_KEY = 'AvailabilityStore';

export function readAvailabilityStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function writeAvailabilityStore(partial) {
  const current = readAvailabilityStore();
  const next = { ...current, ...partial };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function createEmptyWeekSchedule() {
  const day = {};
  WEEKDAY_KEYS.forEach(wk => {
    day[wk] = { [CUTOFF_KEY]: { ...getDefaultCutoffForWeekday(wk) } };
    SCHEDULE_SLOT_KEYS.forEach(sk => {
      day[wk][sk] = false;
    });
  });
  return day;
}

export function createAllTrueWeekSchedule() {
  const day = {};
  WEEKDAY_KEYS.forEach(wk => {
    day[wk] = { [CUTOFF_KEY]: { ...getDefaultCutoffForWeekday(wk) } };
    SCHEDULE_SLOT_KEYS.forEach(sk => {
      day[wk][sk] = true;
    });
  });
  return day;
}

export function generateMockLiveInfoForMonth(monthStart) {
  const start = monthStart.startOf('month');
  const days = start.daysInMonth();
  const liveInfo = {};
  for (let d = 1; d <= days; d += 1) {
    const date = start.date(d);
    const key = date.format('YYYY-MM-DD');
    const working = (d + date.day()) % 3 !== 0;
    if (!working) {
      liveInfo[key] = { working: false };
    } else {
      const slots = [];
      if (d % 2 === 0) slots.push('ALLDAY');
      else {
        if (d % 5 !== 0) slots.push('MORNING');
        if (d % 7 !== 1) slots.push('AFTERNOON');
        // Spread Evening across the month (same keys as SCHEDULE_SLOT_LABELS / Live Calendar chips).
        if (d % 4 === 1 || d % 4 === 2) slots.push('EVENING');
        if (slots.length === 0) slots.push('MORNING');
      }
      liveInfo[key] = { working: true, slots };
    }
  }
  return liveInfo;
}

/**
 * Same merge as LiveCalendar: fill missing month days from mock data.
 * @param {Record<string, { working?: boolean, slots?: string[] }>} value
 * @param {import('dayjs').Dayjs} dayInMonth any day in the target month
 */
export function mergeLiveInfoWithMockForMonth(value, dayInMonth) {
  const mock = generateMockLiveInfoForMonth(dayInMonth.startOf('month'));
  const merged = { ...(value || {}) };
  Object.keys(mock).forEach(k => {
    if (merged[k] == null) {
      merged[k] = mock[k];
    }
  });
  return merged;
}

/**
 * Resolved day entry as shown on the calendar grid (mock-filled gaps).
 */
export function getResolvedDayLiveInfo(value, day) {
  const merged = mergeLiveInfoWithMockForMonth(value, day);
  const key = day.format('YYYY-MM-DD');
  return merged[key];
}

/**
 * Slot keys persisted for a day (merged mock + stored). Independent of `working`:
 * used for dialog seeding; Live Calendar still only renders chips when `working` is true.
 */
export function getResolvedSlotKeysForDay(value, day) {
  const info = getResolvedDayLiveInfo(value, day);
  if (!info || !Array.isArray(info.slots) || info.slots.length === 0) {
    return [];
  }
  return SCHEDULE_SLOT_KEYS.filter(sk => info.slots.includes(sk));
}

export function generateMockDefaultSchedules() {
  const effectiveDate = dayjs().format('YYYY-MM-DD');
  return [
    {
      id: 'default',
      effectiveDate,
      schedule: createAllTrueWeekSchedule(),
    },
  ];
}
